import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp, TrendingDown, Users, CheckCircle2, ShieldCheck, AlertTriangle,
  Plus, FileSearch, Send, ArrowRight, Clock, Activity,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AppShell from "../components/app/AppShell";
import PageHeader, { StatusPill, EmptyState } from "../components/app/PageHeader";
import RegulatoryUpdates from "../components/app/RegulatoryUpdates";

function MetricCard({ label, value, deltaPct, subtitle, accent, testid }) {
  const positive = deltaPct != null && deltaPct >= 0;
  return (
    <div className="card2 p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="t-label text-slate2">{label}</div>
        {accent}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="t-mono" style={{ fontSize: 32, fontWeight: 700 }}>{value}</span>
        {deltaPct != null && (
          <span className={`t-caption flex items-center ${positive ? "text-emerald2-dark" : "text-rose2"}`}>
            {positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />} {Math.abs(deltaPct)}%
          </span>
        )}
      </div>
      <div className="t-caption mt-1">{subtitle}</div>
    </div>
  );
}

function PassRateRing({ value }) {
  const radius = 22;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (value / 100) * circ;
  const color = value >= 80 ? "#10B981" : value >= 60 ? "#F59E0B" : "#EF4444";
  return (
    <svg width="52" height="52" viewBox="0 0 52 52">
      <circle cx="26" cy="26" r={radius} fill="none" stroke="#E2E8F0" strokeWidth="4" />
      <circle cx="26" cy="26" r={radius} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset} transform="rotate(-90 26 26)" />
    </svg>
  );
}

function ActivityFeed({ items }) {
  if (!items.length) {
    return <EmptyState icon={Activity} title="No activity yet" description="Once you start using Compliche, your firm's activity will appear here." />;
  }
  return (
    <div className="card2 divide-y divide-border2" data-testid="activity-feed">
      <div className="p-5 flex items-center justify-between">
        <h3 className="t-h3">Recent activity</h3>
        <Link to="/app/audit-trail" className="t-body-sm text-indigo2 hover:underline">View full audit trail →</Link>
      </div>
      {items.slice(0, 12).map((e) => (
        <div key={e.event_id} className="px-5 py-3.5 hover:bg-surface" data-testid={`activity-${e.event_id}`}>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-indigo2-50 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Activity size={14} className="text-indigo2" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="t-body-sm">{e.action}</div>
              <div className="t-caption mt-0.5">
                {e.user_email || "system"} · {new Date(e.timestamp).toLocaleString()}
              </div>
            </div>
            {e.severity === "warning" && <span className="badge2 badge-pending">Warning</span>}
            {e.severity === "critical" && <span className="badge2 badge-failed">Critical</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function ComplianceHealth({ data }) {
  if (!data || data.total === 0) {
    return (
      <div className="card2 p-6" data-testid="compliance-health-empty">
        <h3 className="t-h3 mb-2">Compliance health</h3>
        <p className="t-body-sm text-slate2 mb-4">Run your first compliance check to populate this widget.</p>
        <Link to="/app/compliance" className="btn btn-secondary btn-sm">Run a check <ArrowRight size={14} /></Link>
      </div>
    );
  }
  return (
    <div className="card2 p-6" data-testid="compliance-health">
      <h3 className="t-h3 mb-4">Compliance health</h3>
      <div className="grid grid-cols-2 gap-4 mb-5">
        <div>
          <div className="t-label text-slate2 mb-1">Avg score</div>
          <div className="t-mono" style={{ fontSize: 28, fontWeight: 700 }}>{data.avg_score}</div>
        </div>
        <div>
          <div className="t-label text-slate2 mb-1">Checks run</div>
          <div className="t-mono" style={{ fontSize: 28, fontWeight: 700 }}>{data.total}</div>
        </div>
        <div>
          <div className="t-label text-slate2 mb-1">Need review</div>
          <div className="t-h3 text-amber2-700">{data.review_count}</div>
        </div>
        <div>
          <div className="t-label text-slate2 mb-1">Failed</div>
          <div className="t-h3 text-rose2">{data.fail_count}</div>
        </div>
      </div>
      <div className="space-y-3">
        {data.by_ruleset.map((rs) => (
          <div key={rs.ruleset}>
            <div className="flex justify-between t-caption mb-1">
              <span className="uppercase">{rs.ruleset}</span><span>{rs.pass_rate}% pass</span>
            </div>
            <div className="h-1.5 bg-border2 rounded-full overflow-hidden">
              <div className="h-full bg-emerald2 rounded-full" style={{ width: `${rs.pass_rate}%` }} />
            </div>
          </div>
        ))}
      </div>
      <Link to="/app/compliance" className="btn btn-secondary btn-sm btn-block mt-5">Run a check <ArrowRight size={14} /></Link>
    </div>
  );
}

function PipelineColumn({ status, items }) {
  const titles = {
    invited: "Invited", started: "Started", in_progress: "In progress",
    awaiting_review: "Awaiting review", complete: "Complete", stalled: "Stalled",
  };
  return (
    <div className="bg-surface rounded-lg border border-border2 p-3 min-h-[160px]" data-testid={`pipeline-${status}`}>
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="t-label text-slate2">{titles[status]}</div>
        <span className="t-caption t-mono">{items.length}</span>
      </div>
      <div className="space-y-2">
        {items.slice(0, 4).map((c) => (
          <Link key={c.id} to={`/app/clients/${c.id}`} className="block bg-white rounded-md border border-border2 p-3 hover:border-indigo2 transition-colors">
            <div className="t-body-sm font-medium truncate">{c.first_name} {c.last_name}</div>
            <div className="t-caption truncate">{c.flow_name}</div>
          </Link>
        ))}
        {items.length === 0 && <div className="t-caption text-muted2 px-1 py-2">None</div>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [activity, setActivity] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [health, setHealth] = useState(null);
  const [wizard, setWizard] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [m, a, p, h, w] = await Promise.all([
          api.get("/api/dashboard/metrics"),
          api.get("/api/dashboard/activity?limit=15"),
          api.get("/api/dashboard/pipeline"),
          api.get("/api/dashboard/compliance-health"),
          api.get("/api/firm/wizard"),
        ]);
        setMetrics(m.data); setActivity(a.data); setPipeline(p.data); setHealth(h.data); setWizard(w.data);
      } catch {}
    })();
  }, []);

  // Redirect to wizard if not completed/dismissed (handled in App.js — here we show a banner)
  return (
    <AppShell>
      <PageHeader
        title={`Welcome back, ${user.first_name}`}
        subtitle={user.firm_name}
        actions={
          <>
            <Link to="/app/flows/new" className="btn btn-primary"><Plus size={16} />New onboarding flow</Link>
            <Link to="/app/compliance" className="btn btn-secondary"><FileSearch size={16} />Run check</Link>
            <Link to="/app/clients?send=1" className="btn btn-ghost"><Send size={16} />Send invite</Link>
          </>
        }
      />

      {wizard && !wizard.completed && !wizard.dismissed && (
        <div className="card2 p-5 mb-6 flex flex-col md:flex-row md:items-center gap-4 border-l-4" style={{ borderLeftColor: "#4F46E5" }} data-testid="wizard-banner">
          <div className="flex-1">
            <h3 className="t-h4 mb-1">Finish setting up your firm</h3>
            <p className="t-body-sm text-slate2">Complete your 5-step setup to unlock all Compliche features.</p>
          </div>
          <Link to="/onboarding" className="btn btn-primary">Resume setup <ArrowRight size={16} /></Link>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <MetricCard testid="metric-active" label="Active Onboardings" value={metrics?.active_onboardings?.value ?? "—"} deltaPct={metrics?.active_onboardings?.delta_pct} subtitle="journeys in progress" accent={<Users size={18} className="text-indigo2" />} />
        <MetricCard testid="metric-complete" label="Completed This Month" value={metrics?.completed_month?.value ?? "—"} deltaPct={metrics?.completed_month?.delta_pct} subtitle="vs last month" accent={<CheckCircle2 size={18} className="text-emerald2-dark" />} />
        <MetricCard testid="metric-passrate" label="Pass Rate" value={`${metrics?.compliance_pass_rate?.value ?? 0}%`} subtitle={`${metrics?.compliance_pass_rate?.checks_run ?? 0} checks this month`} accent={<PassRateRing value={metrics?.compliance_pass_rate?.value ?? 0} />} />
        <MetricCard testid="metric-pending" label="Pending Actions" value={metrics?.pending_actions?.value ?? 0} subtitle="require attention" accent={<AlertTriangle size={18} className={metrics?.pending_actions?.value > 0 ? "text-amber2-700" : "text-muted2"} />} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-6 mb-8">
        <ActivityFeed items={activity} />
        <ComplianceHealth data={health} />
      </div>

      <div className="mb-8">
        <RegulatoryUpdates />
      </div>

      <h2 className="t-h2 mb-4">Client pipeline</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 mb-8">
        {pipeline && ["invited", "started", "in_progress", "awaiting_review", "complete", "stalled"].map((s) => (
          <PipelineColumn key={s} status={s} items={pipeline[s] || []} />
        ))}
      </div>
    </AppShell>
  );
}
